import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import { useToast } from "@/client/components/common/toast"
import type { Assignment, AssignmentSubmission, Course } from "@/lib/supabase/types"
import styles from "./assignment-page.module.css"

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
)

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
)

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
)

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="12" y1="18" x2="12" y2="12"/>
    <line x1="9" y1="15" x2="15" y2="15"/>
  </svg>
)

type FilterTab = "all" | "pending" | "submitted" | "graded"
type DisplayStatus = "pending" | "submitted" | "graded" | "overdue"

interface AssignmentDisplay {
  assignment: Assignment
  submission: AssignmentSubmission | null
  courseTitle: string
  displayStatus: DisplayStatus
}

function getDisplayStatus(assignment: Assignment, submission: AssignmentSubmission | null): DisplayStatus {
  if (submission) {
    if (submission.status === "graded") return "graded"
    return "submitted"
  }
  if (assignment.deadline && new Date(assignment.deadline) < new Date()) return "overdue"
  return "pending"
}

function getStatusBadge(status: DisplayStatus) {
  const map: Record<DisplayStatus, { className: string; label: string }> = {
    pending: { className: styles.pending, label: "待提交" },
    submitted: { className: styles.submitted, label: "已提交" },
    graded: { className: styles.graded, label: "已批改" },
    overdue: { className: styles.overdue, label: "已逾期" },
  }
  const { className, label } = map[status]
  return <span className={`${styles.statusBadge} ${className}`}>{label}</span>
}

function getDueDateClass(status: DisplayStatus): string {
  if (status === "overdue") return `${styles.dueDate} ${styles.danger}`
  if (status === "pending") return `${styles.dueDate} ${styles.warning}`
  return styles.dueDate
}

function AssignmentLoading() {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.backBtn}><BackIcon /></div>
        <span className={styles.headerTitle}>作业中心</span>
      </div>
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <LoadingSkeleton variant="text" width="50%" height={24} />
          <LoadingSkeleton variant="text" width="70%" height={14} style={{ marginTop: 8 }} />
        </div>
      </div>
      <div style={{ padding: 16 }}>
        <LoadingSkeleton variant="text" width="100%" height={60} style={{ marginTop: 8 }} />
        <LoadingSkeleton variant="text" width="100%" height={60} style={{ marginTop: 8 }} />
      </div>
    </div>
  )
}

export default function AssignmentPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const toast = useToast()

  const [items, setItems] = useState<AssignmentDisplay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all")

  const fetchData = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase || !user) { setError("请先登录"); setLoading(false); return }

    setLoading(true)
    setError(null)

    try {
      const [assignmentsResult, submissionsResult, enrollmentsResult] = await Promise.all([
        supabase.from("assignments").select("*").order("deadline", { ascending: true, nullsFirst: false }),
        supabase.from("assignment_submissions").select("*").eq("user_id", user.id),
        supabase.from("enrollments").select("course_id").eq("user_id", user.id).eq("status", "active"),
      ])

      const assignments = (assignmentsResult.data ?? []) as Assignment[]
      const submissions = (submissionsResult.data ?? []) as AssignmentSubmission[]
      const enrollments = enrollmentsResult.data ?? []

      const enrolledCourseIds = new Set(enrollments.map((e) => e.course_id))
      const enrolledAssignments = assignments.filter((a) => enrolledCourseIds.has(a.course_id))

      const submissionMap = new Map<string, AssignmentSubmission>()
      for (const s of submissions) {
        submissionMap.set(s.assignment_id, s)
      }

      const courseIds = [...new Set(enrolledAssignments.map((a) => a.course_id))]
      const courseMap = new Map<string, string>()
      if (courseIds.length > 0) {
        const { data: courses } = await supabase
          .from("courses")
          .select("id, title")
          .in("id", courseIds)
        for (const c of courses ?? []) {
          courseMap.set(c.id, c.title)
        }
      }

      const displayItems: AssignmentDisplay[] = enrolledAssignments.map((a) => {
        const submission = submissionMap.get(a.id) ?? null
        return {
          assignment: a,
          submission,
          courseTitle: courseMap.get(a.course_id) ?? "未知课程",
          displayStatus: getDisplayStatus(a, submission),
        }
      })

      setItems(displayItems)
    } catch {
      setError("加载失败，请重试")
      toast.error("加载作业失败")
    } finally {
      setLoading(false)
    }
  }, [user, toast])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading) return <AssignmentLoading />

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}><BackIcon /></button>
          <span className={styles.headerTitle}>作业中心</span>
        </div>
        <div style={{ padding: "60px 16px", textAlign: "center" }}>
          <p style={{ fontSize: 16, color: "var(--t2)", marginBottom: 16 }}>{error}</p>
          <button onClick={fetchData}
            style={{ padding: "10px 24px", borderRadius: 10, border: "1px solid var(--bd)", background: "var(--bg3)", color: "var(--t)", cursor: "pointer", fontSize: 14 }}>
            重新加载
          </button>
        </div>
      </div>
    )
  }

  const filtered = activeFilter === "all"
    ? items
    : items.filter((i) => i.displayStatus === activeFilter)

  const pendingCount = items.filter((i) => i.displayStatus === "pending").length
  const submittedCount = items.filter((i) => i.displayStatus === "submitted").length
  const gradedCount = items.filter((i) => i.displayStatus === "graded").length

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <span className={styles.headerTitle}>作业中心</span>
      </div>

      {/* Hero Section */}
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <div className={styles.heroIcon}>
            <EditIcon />
          </div>
          <h1 className={styles.heroTitle}>我的作业</h1>
          <p className={styles.heroSubtitle}>及时完成作业，巩固学习成果</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{pendingCount}</div>
          <div className={styles.statLabel}>待完成</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{submittedCount}</div>
          <div className={styles.statLabel}>待批改</div>
        </div>
        <div className={`${styles.statCard} ${styles.highlight}`}>
          <div className={styles.statValue}>{gradedCount}</div>
          <div className={styles.statLabel}>已完成</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className={styles.filterTabs}>
        {(["all", "pending", "submitted", "graded"] as const).map((tab) => (
          <button
            key={tab}
            className={`${styles.filterTab} ${activeFilter === tab ? styles.active : ""}`}
            onClick={() => setActiveFilter(tab)}
          >
            {tab === "all" ? "全部" : tab === "pending" ? "待提交" : tab === "submitted" ? "已提交" : "已批改"}
          </button>
        ))}
      </div>

      {/* Assignment List */}
      <div className={styles.assignmentList}>
        {filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <EmptyIcon />
            </div>
            <h3 className={styles.emptyTitle}>暂无作业</h3>
            <p className={styles.emptyDesc}>
              {items.length === 0 ? "报名课程后，作业将在此显示" : "当前筛选条件下没有作业"}
            </p>
          </div>
        ) : (
          filtered.map((item) => {
            const { assignment, submission, courseTitle, displayStatus } = item
            return (
              <div
                key={assignment.id}
                className={`${styles.assignmentItem} ${displayStatus === "overdue" ? styles.overdue : ""} ${displayStatus === "pending" ? styles.pending : ""} ${displayStatus === "submitted" || displayStatus === "graded" ? styles.submitted : ""}`}
              >
                <div className={styles.assignmentHeader}>
                  <div className={`${styles.assignmentIcon} ${styles.write}`}>
                    <EditIcon />
                  </div>
                  <div className={styles.assignmentInfo}>
                    <span className={styles.assignmentCourse}>{courseTitle}</span>
                    <h3 className={styles.assignmentTitle}>{assignment.title}</h3>
                    <div className={styles.assignmentMeta}>
                      {assignment.deadline && (
                        <span>
                          <ClockIcon />
                          {assignment.deadline.slice(0, 10)}
                        </span>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(displayStatus)}
                </div>

                <div className={styles.assignmentBody}>
                  <p className={styles.assignmentDesc}>{assignment.description}</p>

                  {displayStatus === "graded" && submission?.score !== null && submission?.score !== undefined && (
                    <div className={styles.gradeCard}>
                      <div className={styles.gradeHeader}>
                        <span className={styles.gradeTitle}>作业成绩</span>
                        <div className={styles.gradeScore}>
                          <span className={styles.gradeValue}>{submission.score}</span>
                          <span className={styles.gradeMax}>/{assignment.max_score}</span>
                        </div>
                      </div>
                      <div className={styles.gradeBar}>
                        <div
                          className={styles.gradeFill}
                          style={{ width: `${(submission.score / assignment.max_score) * 100}%` }}
                        />
                      </div>
                      {submission.feedback && (
                        <div className={styles.gradeFeedback}>{submission.feedback}</div>
                      )}
                    </div>
                  )}

                  <div className={styles.assignmentFooter}>
                    <span className={getDueDateClass(displayStatus)}>
                      <CalendarIcon />
                      {displayStatus === "overdue"
                        ? "已逾期"
                        : displayStatus === "pending"
                          ? "即将截止"
                          : "截止日期"}
                      ：{assignment.deadline ? assignment.deadline.slice(0, 10) : "无期限"}
                    </span>
                    <button className={`${styles.actionBtn} ${displayStatus === "graded" ? styles.secondary : ""}`}>
                      {displayStatus === "pending"
                        ? "立即提交"
                        : displayStatus === "submitted"
                          ? "查看详情"
                          : "复习作业"}
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
